import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { gbp } from "@/components/whatsapp/WaCostsTab";
import { WaAttribution } from "@/components/whatsapp/WaAttribution";

const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };
const dt = (s) => (s ? new Date(s).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—");

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-lg font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

export function WaStatsGrid({ s, id }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`wa-an-stats-${id}`}>
      <Stat label="Recipients" value={s.recipients} testid={`wa-an-recipients-${id}`} />
      <Stat label="Delivered" value={s.delivered} sub={`${s.rates.delivery}% of ${s.sent} sent`} testid={`wa-an-delivered-${id}`} />
      <Stat label="Read receipts" value={s.read} sub={`${s.rates.read_receipt}% of delivered`} testid={`wa-an-read-${id}`} />
      <Stat label="Failed" value={s.failed} testid={`wa-an-failed-${id}`} />
      <Stat label="Unique clicks" value={s.unique_clicks} sub={`${s.rates.click}% · ${s.total_clicks} total`} testid={`wa-an-clicks-${id}`} />
      <Stat label="Filtered clicks" value={s.filtered_clicks.bot + s.filtered_clicks.preview} sub={`${s.filtered_clicks.preview} previews · ${s.filtered_clicks.bot} bots`} testid={`wa-an-filtered-${id}`} />
      <Stat label="Replies" value={s.replies} sub={`${s.rates.reply}% of delivered`} testid={`wa-an-replies-${id}`} />
      <Stat label="Cost ex VAT" value={gbp(s.cost.actual_ex_vat)} sub={s.cost.estimate_ex_vat != null ? `estimate ${gbp(s.cost.estimate_ex_vat)}` : `${s.cost.billable_messages} billable`} testid={`wa-an-cost-${id}`} />
      {s.attribution && <Stat label="Cost per enquiry" value={s.cost_per_enquiry != null ? gbp(s.cost_per_enquiry) : "—"} testid={`wa-an-cpe-${id}`} />}
      {s.attribution && <Stat label="Cost per sale" value={s.cost_per_sale != null ? gbp(s.cost_per_sale) : "—"} testid={`wa-an-cps-${id}`} />}
    </div>
  );
}

function Links({ c, onChanged }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ link_key: "", destination_url: "" });
  const load = () => api.get(`/wa/campaigns/${c.id}/links`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => { try { await api.post(`/wa/campaigns/${c.id}/links`, f); setF({ link_key: "", destination_url: "" }); load(); onChanged(); } catch (e) { toast.error(err(e)); } };
  const del = async (l) => { try { await api.delete(`/wa/campaigns/${c.id}/links/${l.id}`); load(); onChanged(); } catch (e) { toast.error(err(e)); } };
  return (
    <div data-testid={`wa-an-links-${c.id}`}>
      <div className="text-xs font-semibold text-slate-700 mb-1">Tracked links</div>
      {items.map((l) => (
        <div key={l.id} className="flex items-center gap-2 text-xs text-slate-600 py-0.5" data-testid={`wa-an-link-${l.id}`}>
          <b>{l.link_key}</b><span className="truncate">{l.destination_url}</span>
          <button onClick={() => del(l)} className="text-slate-400 hover:text-rose-500 cursor-pointer" data-testid={`wa-an-link-del-${l.id}`}>Remove</button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 mt-1">
        <Input className="h-8 w-36" placeholder="Name, e.g. Offer" value={f.link_key} onChange={(e) => setF({ ...f, link_key: e.target.value })} data-testid={`wa-an-link-key-${c.id}`} />
        <Input className="h-8 flex-1 min-w-[200px]" placeholder="https://…" value={f.destination_url} onChange={(e) => setF({ ...f, destination_url: e.target.value })} data-testid={`wa-an-link-url-${c.id}`} />
        <Button size="sm" variant="outline" disabled={!f.link_key || !f.destination_url} onClick={add} data-testid={`wa-an-link-add-${c.id}`}>Add link</Button>
      </div>
    </div>
  );
}

function Recipients({ c, reloadKey }) {
  const [flt, setFlt] = useState({ status: "", clicked: "", replied: "", q: "" });
  const [d, setD] = useState(null);
  const load = () => api.get(`/wa/campaigns/${c.id}/recipients`, { params: { status: flt.status || undefined, clicked: flt.clicked || undefined, replied: flt.replied || undefined, q: flt.q || undefined, limit: 50 } })
    .then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [reloadKey, flt.status, flt.clicked, flt.replied]); // eslint-disable-line react-hooks/exhaustive-deps
  const sel = (k, opts) => (
    <select value={flt[k]} onChange={(e) => setFlt({ ...flt, [k]: e.target.value })} className="rounded-md border border-slate-200 px-2 h-8 text-xs" data-testid={`wa-an-filter-${k}-${c.id}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  if (!d) return null;
  return (
    <div data-testid={`wa-an-recipients-table-${c.id}`}>
      <div className="flex flex-wrap gap-2 mb-2">
        {sel("status", [["", "Any status"], ["sent", "Sent"], ["delivered", "Delivered"], ["read", "Read"], ["failed", "Failed"], ["queued", "Queued"]])}
        {sel("clicked", [["", "Clicked or not"], ["true", "Clicked"], ["false", "Not clicked"]])}
        {sel("replied", [["", "Replied or not"], ["true", "Replied"], ["false", "No reply"]])}
        <Input className="h-8 w-48" placeholder="Name, number, Superchat ID" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} data-testid={`wa-an-search-${c.id}`} />
        <span className="text-xs text-slate-400 self-center" data-testid={`wa-an-total-${c.id}`}>{d.total} recipients</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400">{["Name", "Number", "Status", "Sent", "Delivered", "Read", "Clicks", "Replied"].map((h) => <th key={h} className="py-1 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {d.items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`wa-an-row-${r.id}`}>
                <td className="py-1 pr-3">{r.customer_id ? <a href={`/contacts/${r.customer_id}`} className="text-indigo-600 hover:underline">{r.name || "No name"}</a> : r.name || "No name"}</td>
                <td className="pr-3 whitespace-nowrap">{r.msisdn}</td>
                <td className="pr-3">{r.status}{r.error?.title ? ` · ${r.error.title}` : ""}</td>
                <td className="pr-3 whitespace-nowrap">{dt(r.sent_at)}</td>
                <td className="pr-3 whitespace-nowrap">{dt(r.delivered_at)}</td>
                <td className="pr-3 whitespace-nowrap">{r.read_at ? dt(r.read_at) : "no receipt"}</td>
                <td className="pr-3">{r.clicks || 0}</td>
                <td className="pr-3 whitespace-nowrap">{dt(r.replied_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WaCampaignAnalytics({ c, simulator }) {
  const [s, setS] = useState(null);
  const [key, setKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/wa/campaigns/${c.id}/analytics`).then((r) => setS(r.data)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const sim = async (clear) => {
    setBusy(true);
    try { clear ? await api.delete(`/wa/sim/campaigns/${c.id}`) : await api.post(`/wa/sim/campaigns/${c.id}/run`); toast.success(clear ? "Test data cleared" : "Test delivery simulated"); setKey(key + 1); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get(`/wa/campaigns/${c.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `whatsapp-${c.id}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Export failed"); }
  };
  if (!s) return null;
  return (
    <div className="mt-3 space-y-3 rounded-xl border border-slate-200 p-3" data-testid={`wa-an-panel-${c.id}`}>
      {s.simulated && <div className="text-[11px] rounded-md bg-violet-50 text-violet-700 px-2 py-1" data-testid={`wa-an-sim-banner-${c.id}`}>Test data from the simulator. Nothing was sent to anyone.</div>}
      <WaStatsGrid s={s} id={c.id} />
      {Object.keys(s.failed_reasons).length > 0 && <div className="text-xs text-rose-600" data-testid={`wa-an-reasons-${c.id}`}>Failed: {Object.entries(s.failed_reasons).map(([k, v]) => `${k} (${v})`).join(" · ")}</div>}
      {s.links.length > 0 && <div className="text-xs text-slate-600" data-testid={`wa-an-link-stats-${c.id}`}>{s.links.map((l) => `${l.link_key}: ${l.unique_clicks} people, ${l.clicks} clicks`).join(" · ")}</div>}
      <p className="text-[11px] text-slate-400">{s.read_note} Link previews and bots are excluded from clicks.</p>
      <Links c={c} onChanged={() => setKey(key + 1)} />
      <WaAttribution c={{ ...c, archived_at: s.archived_at }} onChanged={() => setKey(key + 1)} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid={`wa-an-export-${c.id}`}>Export CSV</Button>
        {simulator && <>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => sim(false)} data-testid={`wa-an-sim-run-${c.id}`}>Simulate delivery (test)</Button>
          {s.simulated && <Button size="sm" variant="ghost" disabled={busy} onClick={() => sim(true)} data-testid={`wa-an-sim-clear-${c.id}`}>Clear test data</Button>}
        </>}
      </div>
      <Recipients c={c} reloadKey={key} />
    </div>
  );
}
